import { useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpRight, BarChart3, Brush, Circle, Crosshair, Highlighter, Info, Layers, MapPin, Minus, MoveDiagonal, PenLine, Pencil, Route, Search as SearchLucide, Square, StickyNote, Tag, Target, Triangle, TrendingUp, Type, Waves, type LucideIcon } from 'lucide-react';
import { Drawer } from 'vaul';
import type { SettingsAdapter, TradingViewWidget } from '../types/charting-library';
import type { JsonValue } from '../types/settings';

interface FavoriteDrawingToolbarProps {
  widget: TradingViewWidget | null;
  settingsAdapter: SettingsAdapter;
  leadingControls?: ReactNode;
  trailingControls?: ReactNode;
}

interface ToolOption {
  id: string;
  label: string;
  group: ToolGroup;
  icon: ToolIconName;
}

type ToolGroup = 'Trend Lines' | 'Gann and Fibonacci' | 'Patterns' | 'Forecasting and Measurement' | 'Geometric Shapes' | 'Annotation Tools';
type ToolIconName = 'trend' | 'horizontal' | 'vertical' | 'cross' | 'ray' | 'info' | 'extended' | 'angle' | 'horizontalRay' | 'parallel' | 'regression' | 'flat' | 'disjoint' | 'fibRetracement' | 'fibExtension' | 'fibChannel' | 'fibTime' | 'fibCircle' | 'fibSpiral' | 'fibFan' | 'gannBox' | 'pitchfork' | 'schiffPitchfork' | 'pattern' | 'trianglePattern' | 'elliott' | 'cycle' | 'sine' | 'priceRange' | 'dateRange' | 'datePriceRange' | 'longPosition' | 'shortPosition' | 'forecast' | 'projection' | 'barsPattern' | 'volumeProfile' | 'brush' | 'highlighter' | 'path' | 'curve' | 'rectangle' | 'rotatedRectangle' | 'ellipse' | 'triangle' | 'arc' | 'text' | 'anchoredText' | 'note' | 'signpost' | 'arrowUp' | 'arrowDown' | 'priceLabel' | 'arrowMarker';

const TOOL_OPTIONS: ToolOption[] = [
  { id: 'trend_line', label: 'Trend Line', group: 'Trend Lines', icon: 'trend' },
  { id: 'horizontal_line', label: 'Horizontal Line', group: 'Trend Lines', icon: 'horizontal' },
  { id: 'vertical_line', label: 'Vertical Line', group: 'Trend Lines', icon: 'vertical' },
  { id: 'cross_line', label: 'Cross Line', group: 'Trend Lines', icon: 'cross' },
  { id: 'ray', label: 'Ray', group: 'Trend Lines', icon: 'ray' },
  { id: 'info_line', label: 'Info Line', group: 'Trend Lines', icon: 'info' },
  { id: 'extended', label: 'Extended Line', group: 'Trend Lines', icon: 'extended' },
  { id: 'trend_angle', label: 'Trend Angle', group: 'Trend Lines', icon: 'angle' },
  { id: 'horizontal_ray', label: 'Horizontal Ray', group: 'Trend Lines', icon: 'horizontalRay' },
  { id: 'parallel_channel', label: 'Parallel Channel', group: 'Trend Lines', icon: 'parallel' },
  { id: 'regression_trend', label: 'Regression Trend', group: 'Trend Lines', icon: 'regression' },
  { id: 'flat_bottom', label: 'Flat Top/Bottom', group: 'Trend Lines', icon: 'flat' },
  { id: 'disjoint_angle', label: 'Disjoint Channel', group: 'Trend Lines', icon: 'disjoint' },

  { id: 'fib_retracement', label: 'Fib Retracement', group: 'Gann and Fibonacci', icon: 'fibRetracement' },
  { id: 'fib_trend_ext', label: 'Trend-Based Fib Extension', group: 'Gann and Fibonacci', icon: 'fibExtension' },
  { id: 'fib_channel', label: 'Fib Channel', group: 'Gann and Fibonacci', icon: 'fibChannel' },
  { id: 'fib_timezone', label: 'Fib Time Zone', group: 'Gann and Fibonacci', icon: 'fibTime' },
  { id: 'fib_trend_time', label: 'Trend-Based Fib Time', group: 'Gann and Fibonacci', icon: 'fibTime' },
  { id: 'fib_circles', label: 'Fib Circles', group: 'Gann and Fibonacci', icon: 'fibCircle' },
  { id: 'fib_spiral', label: 'Fib Spiral', group: 'Gann and Fibonacci', icon: 'fibSpiral' },
  { id: 'fib_speed_resist_fan', label: 'Fib Speed Resistance Fan', group: 'Gann and Fibonacci', icon: 'fibFan' },
  { id: 'gannbox', label: 'Gann Box', group: 'Gann and Fibonacci', icon: 'gannBox' },
  { id: 'gannbox_square', label: 'Gann Square', group: 'Gann and Fibonacci', icon: 'gannBox' },
  { id: 'gannbox_fixed', label: 'Gann Fixed', group: 'Gann and Fibonacci', icon: 'gannBox' },
  { id: 'gannbox_fan', label: 'Gann Fan', group: 'Gann and Fibonacci', icon: 'fibFan' },
  { id: 'pitchfork', label: 'Pitchfork', group: 'Gann and Fibonacci', icon: 'pitchfork' },
  { id: 'schiff_pitchfork', label: 'Schiff Pitchfork', group: 'Gann and Fibonacci', icon: 'schiffPitchfork' },
  { id: 'schiff_pitchfork_modified', label: 'Modified Schiff Pitchfork', group: 'Gann and Fibonacci', icon: 'schiffPitchfork' },
  { id: 'inside_pitchfork', label: 'Inside Pitchfork', group: 'Gann and Fibonacci', icon: 'pitchfork' },

  { id: 'xabcd_pattern', label: 'XABCD Pattern', group: 'Patterns', icon: 'pattern' },
  { id: 'cypher_pattern', label: 'Cypher Pattern', group: 'Patterns', icon: 'pattern' },
  { id: 'abcd_pattern', label: 'ABCD Pattern', group: 'Patterns', icon: 'pattern' },
  { id: 'triangle_pattern', label: 'Triangle Pattern', group: 'Patterns', icon: 'trianglePattern' },
  { id: '3divers_pattern', label: 'Three Drives Pattern', group: 'Patterns', icon: 'pattern' },
  { id: 'head_and_shoulders', label: 'Head and Shoulders', group: 'Patterns', icon: 'pattern' },
  { id: 'elliott_impulse_wave', label: 'Elliott Impulse Wave', group: 'Patterns', icon: 'elliott' },
  { id: 'elliott_triangle_wave', label: 'Elliott Triangle Wave', group: 'Patterns', icon: 'elliott' },
  { id: 'elliott_triple_combo', label: 'Elliott Triple Combo', group: 'Patterns', icon: 'elliott' },
  { id: 'elliott_correction', label: 'Elliott Correction', group: 'Patterns', icon: 'elliott' },
  { id: 'elliott_double_combo', label: 'Elliott Double Combo', group: 'Patterns', icon: 'elliott' },
  { id: 'cyclic_lines', label: 'Cyclic Lines', group: 'Patterns', icon: 'cycle' },
  { id: 'time_cycles', label: 'Time Cycles', group: 'Patterns', icon: 'cycle' },
  { id: 'sine_line', label: 'Sine Line', group: 'Patterns', icon: 'sine' },

  { id: 'price_range', label: 'Price Range', group: 'Forecasting and Measurement', icon: 'priceRange' },
  { id: 'date_range', label: 'Date Range', group: 'Forecasting and Measurement', icon: 'dateRange' },
  { id: 'date_and_price_range', label: 'Date and Price Range', group: 'Forecasting and Measurement', icon: 'datePriceRange' },
  { id: 'long_position', label: 'Long Position', group: 'Forecasting and Measurement', icon: 'longPosition' },
  { id: 'short_position', label: 'Short Position', group: 'Forecasting and Measurement', icon: 'shortPosition' },
  { id: 'forecast', label: 'Forecast', group: 'Forecasting and Measurement', icon: 'forecast' },
  { id: 'projection', label: 'Projection', group: 'Forecasting and Measurement', icon: 'projection' },
  { id: 'bars_pattern', label: 'Bars Pattern', group: 'Forecasting and Measurement', icon: 'barsPattern' },
  { id: 'fixed_range_volume_profile', label: 'Fixed Range Volume Profile', group: 'Forecasting and Measurement', icon: 'volumeProfile' },

  { id: 'brush', label: 'Brush', group: 'Geometric Shapes', icon: 'brush' },
  { id: 'highlighter', label: 'Highlighter', group: 'Geometric Shapes', icon: 'highlighter' },
  { id: 'path', label: 'Path', group: 'Geometric Shapes', icon: 'path' },
  { id: 'curve', label: 'Curve', group: 'Geometric Shapes', icon: 'curve' },
  { id: 'rectangle', label: 'Rectangle', group: 'Geometric Shapes', icon: 'rectangle' },
  { id: 'rotated_rectangle', label: 'Rotated Rectangle', group: 'Geometric Shapes', icon: 'rotatedRectangle' },
  { id: 'ellipse', label: 'Ellipse', group: 'Geometric Shapes', icon: 'ellipse' },
  { id: 'triangle', label: 'Triangle', group: 'Geometric Shapes', icon: 'triangle' },
  { id: 'arc', label: 'Arc', group: 'Geometric Shapes', icon: 'arc' },

  { id: 'text', label: 'Text', group: 'Annotation Tools', icon: 'text' },
  { id: 'anchored_text', label: 'Anchored Text', group: 'Annotation Tools', icon: 'anchoredText' },
  { id: 'note', label: 'Note', group: 'Annotation Tools', icon: 'note' },
  { id: 'signpost', label: 'Signpost', group: 'Annotation Tools', icon: 'signpost' },
  { id: 'arrow_up', label: 'Arrow Up', group: 'Annotation Tools', icon: 'arrowUp' },
  { id: 'arrow_down', label: 'Arrow Down', group: 'Annotation Tools', icon: 'arrowDown' },
  { id: 'price_label', label: 'Price Label', group: 'Annotation Tools', icon: 'priceLabel' },
  { id: 'arrow_marker', label: 'Arrow Marker', group: 'Annotation Tools', icon: 'arrowMarker' },
];

const DEFAULT_FAVORITES = ['trend_line', 'horizontal_line', 'ray', 'rectangle'];
const TABS = ['Favorites', 'Trend Lines', 'Gann and Fibonacci', 'Patterns', 'Forecasting and Measurement', 'Geometric Shapes', 'Annotation Tools'] as const;
const drawingToolsSettingsKey = 'chartDrawingTools';
const defaultToolbarPosition = { x: 72, y: 132 };

interface DrawingToolsSettings {
  favorites: string[];
  showFavoriteBar: boolean;
  favoriteBarPosition: { x: number; y: number };
  activeTab: (typeof TABS)[number];
}

export function FavoriteDrawingToolbar({ widget, settingsAdapter, leadingControls, trailingControls }: FavoriteDrawingToolbarProps) {
  const initialSettings = getDrawingToolsSettings(settingsAdapter.initialSettings[drawingToolsSettingsKey]);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number } | null>(null);
  const didMountRef = useRef(false);
  const [position, setPosition] = useState(initialSettings.favoriteBarPosition);
  const [favorites, setFavorites] = useState<string[]>(initialSettings.favorites);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [showFavoriteBar, setShowFavoriteBar] = useState(initialSettings.showFavoriteBar);
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>(initialSettings.activeTab);
  const [query, setQuery] = useState('');

  const favoriteTools = useMemo(() => favorites
    .map((id) => TOOL_OPTIONS.find((tool) => tool.id === id))
    .filter((tool): tool is ToolOption => Boolean(tool)), [favorites]);

  useEffect(() => {
    if (!widget) return undefined;

    const handleSelectedToolChanged = () => {
      const selectedTool = widget.selectedLineTool?.() ?? null;
      setActiveTool(favoriteTools.some((tool) => tool.id === selectedTool) ? selectedTool : null);
    };

    try {
      widget.subscribe('onSelectedLineToolChanged', handleSelectedToolChanged);
      return undefined;
    } catch (err) {
      console.error('selected line tool subscription:', err);
      return undefined;
    }
  }, [favoriteTools, widget]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return undefined;
    }

    const timer = window.setTimeout(() => {
      settingsAdapter.setValue(drawingToolsSettingsKey, {
        favorites,
        showFavoriteBar,
        favoriteBarPosition: position,
        activeTab,
      });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [activeTab, favorites, position, settingsAdapter, showFavoriteBar]);

  function selectTool(tool: ToolOption) {
    try {
      if (activeTool === tool.id) {
        clearActiveTool();
        return;
      }
      widget?.selectLineTool(tool.id);
      setActiveTool(tool.id);
      setIsSheetOpen(false);
    } catch (err) {
      console.error('select drawing tool:', err);
    }
  }

  function clearActiveTool() {
    widget?.selectLineTool('cursor');
    if (widget?.selectedLineTool?.() !== 'cursor') widget?.selectLineTool('arrow_cursor');
    setActiveTool(null);
  }

  function toggleFavorite(tool: ToolOption) {
    setFavorites((current) => (
      current.includes(tool.id)
        ? current.filter((id) => id !== tool.id)
        : [...current, tool.id]
    ));
  }

  function handlePointerDown(event: PointerEvent<HTMLButtonElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
  }

  function handlePointerMove(event: PointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setPosition({
      x: Math.max(8, drag.originX + event.clientX - drag.startX),
      y: Math.max(8, drag.originY + event.clientY - drag.startY),
    });
  }

  function handlePointerUp(event: PointerEvent<HTMLButtonElement>) {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  }

  return (
    <>
      {showFavoriteBar && favoriteTools.length > 0 ? (
        <div className="favorite-drawing-toolbar" style={{ left: position.x, top: position.y }}>
          <button
            className="favorite-drawing-toolbar__drag"
            type="button"
            aria-label="Drag favorite drawing toolbar"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <DragDotsIcon />
          </button>
          {favoriteTools.map((tool) => (
            <button
              key={tool.id}
              className="favorite-drawing-toolbar__button"
              data-active={activeTool === tool.id}
              type="button"
              title={tool.label}
              onClick={() => selectTool(tool)}
            >
              <ToolIcon icon={tool.icon} />
            </button>
          ))}
        </div>
      ) : null}
      <div className="chart-drawing-actions-bar" aria-label="Chart drawing actions">
        {leadingControls}
        <button
          className="chart-drawing-actions-bar__button"
          data-active={isSheetOpen}
          type="button"
          title="Open drawings"
          onClick={() => setIsSheetOpen(true)}
        >
          <Pencil aria-hidden="true" />
          <span className="sr-only">Drawings</span>
        </button>
        {trailingControls}
      </div>
      <Drawer.Root open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay drawing-tools-sheet__overlay" />
          <Drawer.Content className="drawing-tools-sheet" aria-describedby={undefined}>
            <DrawingToolsSheet
              activeTab={activeTab}
              favorites={favorites}
              query={query}
              showFavoriteBar={showFavoriteBar}
              onActiveTabChange={setActiveTab}
              onClose={() => setIsSheetOpen(false)}
              onQueryChange={setQuery}
              onSelectTool={selectTool}
              onShowFavoriteBarChange={setShowFavoriteBar}
              onToggleFavorite={toggleFavorite}
            />
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
}

interface DrawingToolsSheetProps {
  activeTab: (typeof TABS)[number];
  favorites: string[];
  query: string;
  showFavoriteBar: boolean;
  onActiveTabChange(tab: (typeof TABS)[number]): void;
  onClose(): void;
  onQueryChange(query: string): void;
  onSelectTool(tool: ToolOption): void;
  onShowFavoriteBarChange(value: boolean): void;
  onToggleFavorite(tool: ToolOption): void;
}

function DrawingToolsSheet({ activeTab, favorites, query, showFavoriteBar, onActiveTabChange, onClose, onQueryChange, onSelectTool, onShowFavoriteBarChange, onToggleFavorite }: DrawingToolsSheetProps) {
  const normalizedQuery = query.trim().toLowerCase();
  const visibleTools = TOOL_OPTIONS.filter((tool) => {
    if (normalizedQuery) return tool.label.toLowerCase().includes(normalizedQuery);
    if (activeTab === 'Favorites') return favorites.includes(tool.id);
    return tool.group === activeTab;
  });
  const groupedTools = visibleTools.reduce<Array<{ group: ToolGroup; tools: ToolOption[] }>>((groups, tool) => {
    const existing = groups.find((group) => group.group === tool.group);
    if (existing) {
      existing.tools.push(tool);
      return groups;
    }
    groups.push({ group: tool.group, tools: [tool] });
    return groups;
  }, []);

  return (
    <>
      <div className="drawing-tools-sheet__handle" />
      <div className="drawing-tools-sheet__header">
        <Drawer.Title asChild><h2>Drawings</h2></Drawer.Title>
        <button
          className="drawing-tools-sheet__close"
          type="button"
          aria-label="Close drawings"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <label className="drawing-tools-sheet__search">
        <SearchIcon />
        <input value={query} placeholder="Search" onChange={(event) => onQueryChange(event.target.value)} />
      </label>
      <div className="drawing-tools-sheet__tabs">
        {TABS.map((tab) => (
          <button key={tab} type="button" data-active={activeTab === tab} onClick={() => onActiveTabChange(tab)}>{tab}</button>
        ))}
      </div>
      <div className="drawing-tools-sheet__content">
        {groupedTools.length > 0 ? groupedTools.map(({ group, tools }) => (
          <section key={group} className="drawing-tools-sheet__group">
            <h3>{group}</h3>
            <div className="drawing-tools-sheet__grid">
              {tools.map((tool) => (
                <button key={tool.id} className="drawing-tools-sheet__card" type="button" onClick={() => onSelectTool(tool)}>
                  <span className="drawing-tools-sheet__card-icon"><ToolIcon icon={tool.icon} /></span>
                  <span>{tool.label}</span>
                  <span
                    className="drawing-tools-sheet__star"
                    data-active={favorites.includes(tool.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`${favorites.includes(tool.id) ? 'Remove from' : 'Add to'} favorites`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleFavorite(tool);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      event.stopPropagation();
                      onToggleFavorite(tool);
                    }}
                  >
                    ★
                  </span>
                </button>
              ))}
            </div>
          </section>
        )) : <p className="drawing-tools-sheet__empty">No drawing tools found.</p>}
      </div>
      <label className="drawing-tools-sheet__toggle-row">
        <span><PencilStarIcon /> Show favorites on Chart</span>
        <input className="drawing-tools-sheet__switch-input" type="checkbox" checked={showFavoriteBar} onChange={(event) => onShowFavoriteBarChange(event.target.checked)} />
        <span className="drawing-tools-sheet__switch" aria-hidden="true" />
      </label>
    </>
  );
}

const LUCIDE_TOOL_ICONS: Partial<Record<ToolIconName, LucideIcon>> = {
  trend: TrendingUp,
  horizontal: Minus,
  cross: Crosshair,
  ray: ArrowUpRight,
  info: Info,
  extended: MoveDiagonal,
  regression: TrendingUp,
  fibCircle: Target,
  gannBox: Square,
  pattern: Route,
  elliott: Waves,
  cycle: Layers,
  sine: Waves,
  forecast: TrendingUp,
  projection: PenLine,
  barsPattern: BarChart3,
  volumeProfile: BarChart3,
  brush: Brush,
  highlighter: Highlighter,
  path: Route,
  curve: PenLine,
  rectangle: Square,
  ellipse: Circle,
  triangle: Triangle,
  text: Type,
  anchoredText: Type,
  note: StickyNote,
  signpost: MapPin,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  priceLabel: Tag,
  arrowMarker: ArrowUpRight,
};

function ToolIcon({ icon }: { icon: ToolOption['icon'] }) {
  const LucideIcon = LUCIDE_TOOL_ICONS[icon];
  if (LucideIcon) return <LucideIcon aria-hidden="true" />;

  switch (icon) {
    case 'angle':
      return <svg viewBox="0 0 28 28"><path d="M5 22h18" /><path d="M7 22 21 8" /><path d="M12 22a8 8 0 0 1 2.3-5.7" /></svg>;
    case 'vertical':
      return <svg viewBox="0 0 28 28"><path d="M14 5v18" /><circle cx="14" cy="14" r="2.25" /></svg>;
    case 'horizontalRay':
      return <svg viewBox="0 0 28 28"><path d="M4 14h18" /><path d="m18 10 4 4-4 4" /><circle cx="7" cy="14" r="2" /></svg>;
    case 'parallel':
      return <svg viewBox="0 0 28 28"><path d="M7 21 21 7" /><path d="M12 24 24 12" /></svg>;
    case 'flat':
      return <svg viewBox="0 0 28 28"><path d="M5 10h10" /><path d="M5 18h16" /><circle cx="15" cy="10" r="2" /><circle cx="21" cy="18" r="2" /></svg>;
    case 'disjoint':
      return <svg viewBox="0 0 28 28"><path d="M5 21 14 8" /><path d="M14 21 23 8" /><circle cx="5" cy="21" r="2" /><circle cx="14" cy="8" r="2" /><circle cx="14" cy="21" r="2" /><circle cx="23" cy="8" r="2" /></svg>;
    case 'fibRetracement':
      return <svg viewBox="0 0 28 28"><path d="M5 6h18" /><path d="M5 10h18" /><path d="M5 15h18" /><path d="M5 22h18" /><path d="M9 6v16" /></svg>;
    case 'fibExtension':
      return <svg viewBox="0 0 28 28"><path d="M5 20 13 8 23 20" /><path d="M5 8h18" /><path d="M5 14h18" /><path d="M5 20h18" /></svg>;
    case 'fibChannel':
      return <svg viewBox="0 0 28 28"><path d="M5 20 20 5" /><path d="M9 23 24 8" /><path d="M7 10h15" /><path d="M4 16h15" /></svg>;
    case 'fibTime':
      return <svg viewBox="0 0 28 28"><path d="M6 5v18" /><path d="M11 5v18" /><path d="M18 5v18" /><path d="M23 5v18" /></svg>;
    case 'fibSpiral':
      return <svg viewBox="0 0 28 28"><path d="M18 14a4 4 0 1 1-4-4h8a8 8 0 1 1-8-8" /></svg>;
    case 'fibFan':
      return <svg viewBox="0 0 28 28"><path d="M5 23 23 5" /><path d="M5 23 23 13" /><path d="M5 23h18" /><path d="M5 23 13 5" /></svg>;
    case 'pitchfork':
      return <svg viewBox="0 0 28 28"><path d="M14 4v20" /><path d="M6 9v8" /><path d="M22 9v8" /><path d="M6 9 14 4l8 5" /></svg>;
    case 'schiffPitchfork':
      return <svg viewBox="0 0 28 28"><path d="M14 7v17" /><path d="M6 11v8" /><path d="M22 5v8" /><path d="M6 11 14 7l8-2" /></svg>;
    case 'trianglePattern':
      return <svg viewBox="0 0 28 28"><path d="M5 22 14 6l9 16z" /><path d="M8 16h12" /><circle cx="14" cy="6" r="2" /></svg>;
    case 'priceRange':
      return <svg viewBox="0 0 28 28"><path d="M8 5v18" /><path d="m5 8 3-3 3 3" /><path d="m5 20 3 3 3-3" /><path d="M15 7h8" /><path d="M15 21h8" /></svg>;
    case 'dateRange':
      return <svg viewBox="0 0 28 28"><path d="M6 7v14" /><path d="M22 7v14" /><path d="M6 14h16" /><path d="m10 10-4 4 4 4" /><path d="m18 10 4 4-4 4" /></svg>;
    case 'datePriceRange':
      return <svg viewBox="0 0 28 28"><path d="M7 21h15" /><path d="M7 6v15" /><path d="m18 17 4 4-4 4" /><path d="m3 10 4-4 4 4" /><circle cx="7" cy="21" r="2" /><circle cx="22" cy="21" r="2" /><circle cx="7" cy="6" r="2" /></svg>;
    case 'longPosition':
      return <svg viewBox="0 0 28 28"><path d="M14 5v18" /><path d="M14 8h7l-2 4 2 4h-7" /><path d="M14 13H7l2 4-2 4h7" /></svg>;
    case 'shortPosition':
      return <svg viewBox="0 0 28 28"><path d="M14 5v18" /><path d="M14 13h7l-2-4 2-4h-7" /><path d="M14 20H7l2-4-2-4h7" /></svg>;
    case 'rotatedRectangle':
      return <svg viewBox="0 0 28 28"><path d="m14 4 10 10-10 10L4 14z" /></svg>;
    case 'arc':
      return <svg viewBox="0 0 28 28"><path d="M6 22a12 12 0 0 1 16 0" /><circle cx="6" cy="22" r="2" /><circle cx="22" cy="22" r="2" /></svg>;
  }
}

function DragDotsIcon() {
  return <svg viewBox="0 0 12 18"><circle cx="3" cy="3" r="1.4" /><circle cx="9" cy="3" r="1.4" /><circle cx="3" cy="9" r="1.4" /><circle cx="9" cy="9" r="1.4" /><circle cx="3" cy="15" r="1.4" /><circle cx="9" cy="15" r="1.4" /></svg>;
}

function SearchIcon() {
  return <SearchLucide aria-hidden="true" />;
}

function PencilStarIcon() {
  return <svg viewBox="0 0 32 32"><path d="M5 23.5 6.5 18 17 7.5a2.5 2.5 0 0 1 3.5 3.5L10 21.5z" /><path d="M15 9.5 19 13.5" /><path d="m23 17 1.6 3.2 3.5.5-2.5 2.5.6 3.5-3.2-1.7-3.2 1.7.6-3.5-2.5-2.5 3.5-.5z" /></svg>;
}

function getDrawingToolsSettings(value: JsonValue | undefined): DrawingToolsSettings {
  if (!isRecord(value)) return getDefaultDrawingToolsSettings();

  const favorites = Array.isArray(value.favorites)
    ? value.favorites.filter((item): item is string => typeof item === 'string' && TOOL_OPTIONS.some((tool) => tool.id === item))
    : DEFAULT_FAVORITES;
  const position = isRecord(value.favoriteBarPosition)
    ? {
      x: typeof value.favoriteBarPosition.x === 'number' ? value.favoriteBarPosition.x : defaultToolbarPosition.x,
      y: typeof value.favoriteBarPosition.y === 'number' ? value.favoriteBarPosition.y : defaultToolbarPosition.y,
    }
    : defaultToolbarPosition;
  const activeTab = typeof value.activeTab === 'string' && isDrawingToolTab(value.activeTab) ? value.activeTab : 'Trend Lines';

  return {
    favorites: favorites.length > 0 ? favorites : DEFAULT_FAVORITES,
    showFavoriteBar: typeof value.showFavoriteBar === 'boolean' ? value.showFavoriteBar : true,
    favoriteBarPosition: position,
    activeTab,
  };
}

function getDefaultDrawingToolsSettings(): DrawingToolsSettings {
  return {
    favorites: DEFAULT_FAVORITES,
    showFavoriteBar: true,
    favoriteBarPosition: defaultToolbarPosition,
    activeTab: 'Trend Lines',
  };
}

function isRecord(value: JsonValue | undefined): value is { [key: string]: JsonValue } {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isDrawingToolTab(value: string): value is (typeof TABS)[number] {
  return TABS.includes(value as (typeof TABS)[number]);
}
